/* eslint-disable no-restricted-globals */
import { Font, pdf } from '@react-pdf/renderer';
import i18n from 'i18n';
import { AssortmentGetProductsPOSTRequest, ShopAttributeProduct, ShopAttributeProductPageInfo } from 'microshop-api';
import { PDFDocument } from 'pdf-lib';
import PDFMerger from 'pdf-merger-js/browser';
import services from 'services';
import { setConfiguration } from 'services/api';
import { loadFontForPDF } from 'styles/fonts';
import { PdfDocumentData } from './exportPdfTypes';
import { getDocument } from './getDocument';

const PRODUCT_PAGE_SIZE = 12;
type Id = number | string;

export type WorkerInputSearchParams = AssortmentGetProductsPOSTRequest & { productNumbers?: string[] };

export type WorkerInputStart = {
    type: 'start';
    id: Id;
    productSearchParams: WorkerInputSearchParams;
    data: PdfDocumentData;
    noMaxProducts?: boolean;
    token?: string;
    lang?: string | null;
    font?: string;
};
export type WorkerInput =
    | WorkerInputStart
    | {
          type: 'cancel';
          id: Id;
      };

export type ExportWorkerMessage =
    | {
          type: 'progress';
          id: Id;
          payload: number;
      }
    | {
          type: 'result' | 'error';
          id: Id;
          payload: string;
      }
    | {
          type: 'resultBlob';
          payload: Blob;
          id: Id;
      }
    | {
          type: 'cancelled';
          id: Id;
      };

const cancelSignal: Record<Id, boolean> = {};
self.onmessage = async (event: { data: WorkerInput }) => {
    const { data } = event;

    switch (data.type) {
        case 'start':
            createPdfFile(data);
            break;
        case 'cancel':
            cancelSignal[data.id] = true;
            break;
    }
};

async function createPdfFile(data: WorkerInputStart) {
    if (data.token) setConfiguration({ token: data.token });
    const id = data.id;

    cancelSignal[id] = false;
    await i18n.changeLanguage(data.lang ?? 'en');
    let resultBlob: Blob | undefined = await chunkAndMergeProducts(data);

    if (!resultBlob) {
        if (cancelPdfCreation(id)) return; // Cancelled - not an error.

        postMessage({ type: 'error', payload: 'No blob', id });
        return;
    }

    const downloadUrl = URL.createObjectURL(resultBlob);
    postMessage({ type: 'result', payload: downloadUrl, id });
}

function cancelPdfCreation(id: Id) {
    if (cancelSignal[id]) {
        postMessage({ type: 'cancelled', id });
        return true;
    }
    return false;
}

async function getProducts(params: AssortmentGetProductsPOSTRequest): Promise<ShopAttributeProductPageInfo> {
    params.pageSize = PRODUCT_PAGE_SIZE;
    if (!params.page) params.page = 1;

    return await services.assortment.getAttributeProducts(params);
}

async function renderPdf(data: PdfDocumentData, fontFamily: string, pageCount?: number): Promise<Blob> {
    loadFontForPDF(Font, fontFamily);
    data.pageStartingNumber = pageCount;
    const pdfTest = pdf(getDocument(data));

    return pdfTest.toBlob();
}

// Typed post message.
function postMessage(m: ExportWorkerMessage) {
    self.postMessage(m);
}

function getChunkProducts(products: ShopAttributeProduct[] | null | undefined, documentData: PdfDocumentData) {
    if (!products) return [];

    if (documentData.settings.useCompactLayout) {
        return products.map((p) => {
            return {
                ...p,
                variations: [{ variationNumber: p.productNumber, price: p.price, image: p.image }],
            };
        });
    }
    return products;
}

async function getPageCount(blob: Blob) {
    const ab = await blob.arrayBuffer();
    const pdfDocument = await PDFDocument.load(ab);
    const pageCount = pdfDocument.getPageCount();
    return pageCount;
}

type DefaultParams = {
    page: number;
    pageSize: number;
};
const defaultParams: DefaultParams = {
    page: 1,
    pageSize: 10,
};

async function chunkAndMergeProducts(data: WorkerInputStart) {
    const { productNumbers, ...params } = data.productSearchParams;
    const documentData = data.data;
    const id = data.id;

    try {
        /**
         * Order, offer, cart type pdfs will not be split into chunks for now. To implement it we need to merge cartvariation-data with fetched products outside of selector,
         * simliar to what we do with "getMarks" for careinstructions etc.
         */
        if (productNumbers?.length) {
            postMessage({ type: 'progress', payload: 30, id });
            const blob = await renderPdf(
                { ...documentData, products: getChunkProducts(documentData.products, documentData) },
                data.font ?? 'Roboto',
            );
            if (cancelPdfCreation(id)) return;
            return blob;
        }

        /**
         * Split product requests and pdf-renders into chunks and merge them back together when finished.
         */
        postMessage({ type: 'progress', payload: 1, id });

        const noMaxProducts = data.noMaxProducts;
        let attributeProducts: ShopAttributeProduct[] = []; // Need to save chunks in memory so we can extract all marks for careinstructions.
        const parameters = { ...defaultParams, ...params };
        const merger = new PDFMerger();

        let hasMore = true;
        let pageCount = 0;
        let splitIntoChunks = false;
        while (hasMore) {
            const chunkPageInfo = await getProducts(parameters);
            if (chunkPageInfo.items) attributeProducts = [...attributeProducts, ...chunkPageInfo.items];

            // In pdf-files where we have to chunk rendering (more products than PRODUCT_PAGE_SIZE)
            // only the last chunk knows exactly how many pages will be rendered in total. For this reason we cannot render total pages. Current page can still
            // be rendered correct since we can pass in the previous chunks total size.
            if (chunkPageInfo.hasNextPage) splitIntoChunks = true;
            if (cancelPdfCreation(id)) return;

            const chunkDocumentData: PdfDocumentData = {
                ...documentData,
                products: getChunkProducts(chunkPageInfo.items, documentData),
            };

            const chunkBlob = await renderPdf(chunkDocumentData, 'Inter', splitIntoChunks ? pageCount : undefined);
            if (cancelPdfCreation(id)) return;

            const chunkPageCount = await getPageCount(chunkBlob);
            pageCount += chunkPageCount;

            await merger.add(chunkBlob);

            postMessage({ type: 'progress', payload: (100 / (chunkPageInfo.totalPages ?? 1)) * parameters.page, id });
            hasMore = (chunkPageInfo.hasNextPage && (noMaxProducts || parameters.page < 2)) ?? false;
            parameters.page = parameters.page + 1;
        }

        return await merger.saveAsBlob();
    } catch (error) {
        console.error('Worker failed.', { error });
        if (error instanceof Error) {
            cancelSignal[id] = true;
            postMessage({ type: 'error', payload: error.message, id });
        }
    }
}
